import React from 'react';
import '../Cards/Cards.css';
import '../../index.css';

const asArray = (value) => (Array.isArray(value) ? value : []);

const people = (data) => [
    ...asArray(data?.who?.known),
    ...asArray(data?.who?.unknown),
];

const places = (data) => asArray(data?.where?.known);
const timeframeEvents = (data) => asArray(data?.when?.Events);

const relatedItems = (data, keys) => keys.reduce((items, key) => {
    const value = key.split('.').reduce((current, part) => current?.[part], data);
    return [...items, ...asArray(value)];
}, []);

const exploreItems = (data) => ({
    devotionals: relatedItems(data, ['relatedDevotionals', 'devotionals', 'related.devotionals']),
    articles: relatedItems(data, ['relatedArticles', 'articles', 'related.articles']),
    links: relatedItems(data, ['relatedLinks', 'links', 'related.links']),
});

export const hasExploreContent = (data) => {
    const related = exploreItems(data);
    return people(data).length > 0
        || places(data).length > 0
        || Boolean(data?.when?.Written)
        || timeframeEvents(data).length > 0
        || related.devotionals.length > 0
        || related.articles.length > 0
        || related.links.length > 0;
};

const splitPrimaryText = (value) => {
    const text = `${value || ''}`.trim();
    if (!text.includes(':')) return null;

    const [left, ...rest] = text.split(':');
    const title = left.trim();
    const summary = rest.join(':').trim();

    if (!title || !summary) return null;
    return { title, summary };
};

const itemTitle = (item, type) => {
    const explicitTitle = item?.name || item?.title || item?.label;
    if (explicitTitle) return explicitTitle;

    if (type === 'event') {
        const splitText = splitPrimaryText(item?.summary || item?.description || item?.text);
        if (splitText) return splitText.title;
    }

    return item?.summary || item?.description || item?.text || 'Explore';
};

const itemSummary = (item, type) => {
    if (type === 'event') {
        const splitText = splitPrimaryText(item?.summary || item?.description || item?.text);
        if (splitText) return splitText.summary;
    }

    return item?.meaning || item?.summary || item?.description || item?.text || '';
};
const itemHref = (item, type) => item?.href || item?.url || item?.link || (item?.ref
    ? `/${type}/${item.ref}`
    : '#');

function ExploreCard({ item, type, accent }) {
    const title = itemTitle(item, type);
    const summary = itemSummary(item, type);
    const href = itemHref(item, type);

    return (
        <a className="explore-card" href={href} style={{ '--explore-accent': accent }}>
            <span className="explore-card-type">{type}</span>
            <strong className="explore-card-title">{title}</strong>
            {summary ? <span className="explore-card-summary">{summary}</span> : null}
        </a>
    );
}

function ExploreSection({ title, items, type, accent }) {
    if (!items.length) return null;

    return (
        <section className="explore-section">
            <h3>{title}</h3>
            <div className="explore-card-grid">
                {items.map((item, index) => (
                    <ExploreCard
                        key={`${type}-${item?.ref || item?.name || item?.title || index}`}
                        item={item}
                        type={type}
                        accent={accent}
                    />
                ))}
            </div>
        </section>
    );
}

function WrittenSection({ value }) {
    if (!value) return null;

    return (
        <section className="explore-section explore-written-section">
            <h3>Written</h3>
            <div className="explore-written-card">
                <span>{value}</span>
            </div>
        </section>
    );
}

function ExploreList({ data }) {
    const related = exploreItems(data);
    const events = timeframeEvents(data);

    return (
        <div className="explore-panel">
            <WrittenSection value={data?.when?.Written} />
            <ExploreSection title="People" items={people(data)} type="person" accent="var(--color-scheme-5)" />
            <ExploreSection title="Places" items={places(data)} type="location" accent="var(--color-scheme-7)" />
            <ExploreSection title="Related devotionals" items={related.devotionals} type="devotional" accent="var(--color-scheme-3)" />
            <ExploreSection title="Related articles" items={related.articles} type="article" accent="var(--color-scheme-4)" />
            <ExploreSection title="Related links" items={related.links} type="link" accent="var(--color-scheme-6)" />
            <ExploreSection title="Events in this timeframe" items={events} type="event" accent="var(--color-scheme-4)" />
        </div>
    );
}

export default ExploreList;
